/**
 * acium-motion.js
 * Requer Anime.js v3 — carregado via CDN com guard de instância única.
 * Suporta múltiplas instâncias da section na mesma página.
 */

if (!window.__AciumMotionDefined) {
  window.__AciumMotionDefined = true;

  // ─────────────────────────────────────────────────────────────────────────
  // AciumInfoline — infográfico de linha animado
  // ─────────────────────────────────────────────────────────────────────────
  class AciumInfoline {
    constructor(sid, config, svgEl, counterSvgEl) {
      this.sid  = sid;
      this.C    = config;
      this.svg  = svgEl;
      this.cSvg = counterSvgEl;

      // Sistema de coordenadas SVG — atualizados por _computeCoords()
      this.VB_W    = 1240;
      this.VB_H    = 420;
      this.Y_TOP   = 30;
      this.Y_RANGE = 340;
      this.X_START = 90;
      this.X_END   = 940;
      this.NS      = 'http://www.w3.org/2000/svg';

      this.startPt    = null;
      this.endPt      = null;
      this.stepPts    = [];
      this.allPts     = [];
      this.cumLen     = [];
      this.svgLineLen = 0;
      this.line       = null;
      this._svgDefs   = null;

      this.stepRevealed = [];
      this.endRevealed  = false;
      this.cObj         = { val: 0 };
      this.fullVB       = { x: 0, y: 0, w: 1240, h: 420 };
      this.initialVB    = null;
      this.currentVB    = null;
      this.playing      = false;
      this._timeline    = null;

      this._init();
    }

    // ── Mobile detection ──────────────────────────────────────────────────────

    get _mobile() {
      return window.innerWidth < 750;
    }

    // ── Coordenadas ──────────────────────────────────────────────────────────

    _yOf(pct) {
      return this.Y_TOP + (pct / 100) * this.Y_RANGE;
    }

    _computeCoords() {
      const C     = this.C;
      const mob   = this._mobile;
      const above = C.counterPosition === 'above';

      if (mob && !above) {
        // Mobile lateral: infográfico 60% esquerda, contador 40% direita
        this.VB_W    = 760;
        this.VB_H    = 420;
        this.X_START = 40;
        this.X_END   = 410;
        this.Y_TOP   = 30;
        this.Y_RANGE = 340;
      } else if (mob && above) {
        // Mobile acima: gráfico ocupa 100% da largura
        this.VB_W    = 760;
        this.VB_H    = 420;
        this.X_START = 40;
        this.X_END   = 690;
        this.Y_TOP   = 30;
        this.Y_RANGE = 340;
      } else {
        // Desktop: sempre 100% largura
        this.VB_W    = 1240;
        this.VB_H    = 420;
        this.X_START = 90;
        this.X_END   = 940;
        this.Y_TOP   = 30;
        this.Y_RANGE = 340;
      }
      this.fullVB  = { x: 0, y: 0, w: this.VB_W, h: this.VB_H };

      this.startPt = { x: this.X_START, y: this._yOf(C.start.heightPct) };
      this.endPt   = { x: this.X_END,   y: this._yOf(C.end.heightPct)   };
      this.stepPts = C.steps.map((s, k) => ({
        x: this.X_START + (this.X_END - this.X_START) * (k + 1) / (C.steps.length + 1),
        y: this._yOf(s.heightPct),
      }));
      this.allPts = [this.startPt, ...this.stepPts, this.endPt];
    }

    // ── Helpers SVG ──────────────────────────────────────────────────────────

    _el(tag, attrs) {
      const e = document.createElementNS(this.NS, tag);
      for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
      return e;
    }

    _txt(content, attrs) {
      const t = this._el('text', attrs);
      t.textContent = content;
      return t;
    }

    // ── Construtores de elementos ─────────────────────────────────────────────

    /**
     * Ponto com gauge (start ou end).
     * Arquitetura nested group obrigatória:
     *   outer <g transform="translate(cx,cy)"> — posicionamento SVG, nunca animado
     *   inner <g id="...">                     — alvo do Anime.js (scale/opacity)
     */
    _buildGaugePoint(cx, cy, cfg, innerId, innerClass) {
      const C   = this.C;
      const T   = C.typography || {};
      const mob = this._mobile;
      const r   = cfg.r;
      const gr = r * 0.78;
      const sw = r < 40 ? 5 : 8;
      const Circ   = 2 * Math.PI * gr;
      const filled = (cfg.gaugeValue / 100) * Circ;

      const outer = this._el('g', { transform: `translate(${cx},${cy})` });
      const inner = this._el('g', {});
      if (innerId)    inner.id = innerId;
      if (innerClass) inner.setAttribute('class', innerClass);

      inner.appendChild(this._el('circle', {
        r, fill: C.circleFill, stroke: C.circleBorder, 'stroke-width': 1.5,
      }));
      inner.appendChild(this._el('circle', {
        r: gr, fill: 'none', stroke: C.gaugeTrack, 'stroke-width': sw,
        transform: 'rotate(-90)',
      }));
      inner.appendChild(this._el('circle', {
        r: gr, fill: 'none', stroke: C.gaugeFill, 'stroke-width': sw,
        'stroke-dasharray': `${filled.toFixed(2)} ${Circ.toFixed(2)}`,
        transform: 'rotate(-90)',
      }));
      inner.appendChild(this._txt(cfg.label, {
        'text-anchor': 'middle', 'dominant-baseline': 'central',
        'font-size': Math.round(r * 0.40),
        fill: C.textColor,
        'font-family': "'Avenir LT Std', system-ui, sans-serif",
        'font-weight': mob ? (T.circleWeightM || T.circleWeight || '700') : (T.circleWeight || '700'),
      }));

      outer.appendChild(inner);
      return outer;
    }

    _buildStepPoint(cx, cy, s, i) {
      const C   = this.C;
      const sid = this.sid;

      const outer = this._el('g', { transform: `translate(${cx},${cy})` });
      const inner = this._el('g', { id: `acim-step-${i}-${sid}`, class: 'step-inner' });
      inner.style.opacity = '0';

      // Cor de fundo: individual por step-point ou herda global
      const bgColor = (s.bgColor && s.bgColor !== '') ? s.bgColor : C.circleFill;

      inner.appendChild(this._el('circle', {
        r: s.r, fill: bgColor, stroke: C.circleBorder, 'stroke-width': 1.5,
      }));

      // Ícone: imagem uploadada ou caractere
      if (s.image) {
        const sz     = s.iconSize > 0 ? s.iconSize : Math.round(s.r * 1.4);
        const half   = sz / 2;
        const clipId = `acim-clip-${i}-${sid}`;
        const clip   = this._el('clipPath', { id: clipId });
        clip.appendChild(this._el('circle', { r: s.r - 2, cx: '0', cy: '0' }));
        this._svgDefs.appendChild(clip);
        inner.appendChild(this._el('image', {
          href: s.image,
          x: -half, y: -half,
          width: sz, height: sz,
          preserveAspectRatio: 'xMidYMid slice',
          'clip-path': `url(#${clipId})`,
        }));
      } else if (s.icon) {
        inner.appendChild(this._txt(s.icon, {
          'text-anchor': 'middle', 'dominant-baseline': 'central',
          'font-size': s.iconSize > 0 ? s.iconSize : Math.round(s.r * 0.68),
          fill: C.circleBorder,
          'font-family': "'Avenir LT Std', system-ui, sans-serif",
          'font-weight': '700',
        }));
      }

      const T   = C.typography || {};
      const mob = this._mobile;
      const dy  = s.labelPos === 'above' ? -(s.r + 13) : (s.r + 15);

      const slSize    = mob ? (T.stepLabelSizeM    > 0 ? T.stepLabelSizeM    : T.stepLabelSize    || 13) : (T.stepLabelSize    || 13);
      const slWeight  = mob ? (T.stepLabelWeightM  || T.stepLabelWeight  || '400') : (T.stepLabelWeight  || '400');
      const slSpacing = mob ? T.stepLabelSpacingM  : T.stepLabelSpacing;

      inner.appendChild(this._txt(s.label, {
        'text-anchor': 'middle', y: dy,
        'dominant-baseline': s.labelPos === 'above' ? 'auto' : 'hanging',
        'font-size':      slSize,
        'font-weight':    slWeight,
        'letter-spacing': slSpacing,
        fill: C.labelColor,
        'font-family': "'Avenir LT Std', system-ui, sans-serif",
      }));

      outer.appendChild(inner);
      return outer;
    }

    // ── Construção do SVG ─────────────────────────────────────────────────────

    _buildSVG() {
      const C   = this.C;
      const sid = this.sid;
      const mob = this._mobile;

      this.svg.innerHTML  = '';
      this.cSvg.innerHTML = '';

      // Sincronizar viewBox de ambos os SVGs com as dimensões do layout atual
      const vb = `0 0 ${this.VB_W} ${this.VB_H}`;
      this.svg.setAttribute('viewBox', vb);
      this.cSvg.setAttribute('viewBox', vb);

      // Defs para clip paths das imagens (nível raiz do SVG)
      this._svgDefs = this._el('defs', {});
      this.svg.appendChild(this._svgDefs);

      // Polyline
      this.line = this._el('polyline', {
        id: `acim-line-${sid}`,
        points: this.allPts.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' '),
        fill: 'none', stroke: C.lineColor, 'stroke-width': C.lineThickness,
        'stroke-linecap': 'round', 'stroke-linejoin': 'round',
      });
      this.svg.appendChild(this.line);

      // Ponto inicial (sempre visível)
      this.svg.appendChild(
        this._buildGaugePoint(this.startPt.x, this.startPt.y, C.start, null, null)
      );

      // Step-points (revelados pela callback do update)
      C.steps.forEach((s, i) => {
        this.svg.appendChild(
          this._buildStepPoint(this.stepPts[i].x, this.stepPts[i].y, s, i)
        );
      });

      // Ponto final (revelado ao chegar a linha)
      const endOuter = this._buildGaugePoint(
        this.endPt.x, this.endPt.y, C.end,
        `acim-end-${sid}`, 'end-inner'
      );
      endOuter.querySelector(`#acim-end-${sid}`).style.opacity = '0';
      this.svg.appendChild(endOuter);

      // Counter SVG — posição, tamanho e tipografia dependem do layout
      const T     = C.typography || {};
      const above = C.counterPosition === 'above';

      // Tipografia D/M — size: 0 = herda desktop; weight/spacing: direto
      const cSize    = mob ? (T.counterSizeM  || 180) : (T.counterSize  || 86);
      const cWeight  = mob ? (T.counterWeightM  || T.counterWeight  || '900') : (T.counterWeight  || '900');
      const cSpacing = mob ? T.counterSpacingM  : T.counterSpacing;

      const clSize    = mob ? (T.counterLabelSizeM    > 0 ? T.counterLabelSizeM    : T.counterLabelSize    || 14) : (T.counterLabelSize    || 14);
      const clWeight  = mob ? (T.counterLabelWeightM  || T.counterLabelWeight  || '400') : (T.counterLabelWeight  || '400');
      const clSpacing = mob ? T.counterLabelSpacingM  : T.counterLabelSpacing;

      let cxCounter, cyCounter, cFontSize, cLabelY, cAnchor;

      if (above) {
        // Acima: SVG próprio com altura calculada pelo font-size
        cFontSize        = cSize;
        const cVBH       = Math.round(cFontSize * 1.4 + 40);
        cxCounter        = this.VB_W / 2;
        cyCounter        = Math.round(cFontSize * 0.7 + 10);
        cLabelY          = Math.round(cFontSize * 0.55 + 16);
        cAnchor          = 'middle';
        this.cSvg.setAttribute('viewBox', `0 0 ${this.VB_W} ${cVBH}`);
      } else if (mob) {
        // Mobile lateral: contador na zona direita (40% do VB)
        cFontSize        = cSize;
        const zoneLeft   = this.VB_W * 0.6;
        cxCounter        = zoneLeft + (this.VB_W - zoneLeft) / 2;
        cyCounter        = this.VB_H * 0.44;
        cLabelY          = Math.round(cFontSize * 0.55 + 16);
        cAnchor          = 'middle';
      } else {
        // Desktop lateral: contador à direita do círculo final
        cFontSize        = cSize;
        cxCounter        = this.X_END + C.end.r + 20;
        cyCounter        = this.endPt.y;
        cLabelY          = Math.round(cFontSize * 0.55 + 16);
        cAnchor          = 'start';
      }

      const cGroup = this._el('g', { transform: `translate(${cxCounter},${cyCounter})` });
      cGroup.appendChild(this._txt('0', {
        id: `acim-cval-${sid}`,
        'text-anchor':    cAnchor,
        'dominant-baseline': 'central',
        'font-size':      cFontSize,
        'font-weight':    cWeight,
        'letter-spacing': cSpacing,
        fill: C.counterColor,
        'font-family': "'Avenir LT Std', system-ui, sans-serif",
      }));
      cGroup.appendChild(this._txt(C.end.counterLabel, {
        y:                cLabelY,
        'text-anchor':    cAnchor,
        'font-size':      clSize,
        'font-weight':    clWeight,
        'letter-spacing': clSpacing,
        fill: C.counterLabelColor,
        'font-family': "'Avenir LT Std', system-ui, sans-serif",
      }));
      this.cSvg.appendChild(cGroup);
    }

    // ── Comprimentos cumulativos (sincronismo exato da revelação) ─────────────

    _computeLengths() {
      const dist = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);
      this.cumLen = [0];
      for (let i = 1; i < this.allPts.length; i++) {
        this.cumLen.push(this.cumLen[i - 1] + dist(this.allPts[i - 1], this.allPts[i]));
      }
      this.svgLineLen = this.line.getTotalLength();
      this.line.style.strokeDasharray  = this.svgLineLen;
      this.line.style.strokeDashoffset = this.svgLineLen;
    }

    // ── Zoom-out ──────────────────────────────────────────────────────────────

    _setupZoom() {
      const C = this.C;
      this.fullVB = { x: 0, y: 0, w: this.VB_W, h: this.VB_H };

      if (C.zoomOut.enabled) {
        const s  = C.zoomOut.startScale;
        const zw = this.VB_W * s;
        const zh = this.VB_H * s;
        this.initialVB = {
          x: Math.max(0, this.startPt.x - zw * 0.22),
          y: Math.max(0, this.startPt.y - zh * 0.52),
          w: zw, h: zh,
        };
      } else {
        this.initialVB = { ...this.fullVB };
      }
      this.currentVB = { ...this.initialVB };
      this._applyVB();
    }

    _applyVB() {
      this.svg.setAttribute('viewBox',
        `${this.currentVB.x.toFixed(1)} ${this.currentVB.y.toFixed(1)} ` +
        `${this.currentVB.w.toFixed(1)} ${this.currentVB.h.toFixed(1)}`
      );
    }

    // ── Reset ─────────────────────────────────────────────────────────────────

    _reset() {
      const sid = this.sid;
      this.stepRevealed = new Array(this.C.steps.length).fill(false);
      this.endRevealed  = false;
      this.cObj.val     = 0;

      this.line.style.strokeDashoffset = this.svgLineLen;

      this.svg.querySelectorAll('.step-inner, .end-inner').forEach(e => {
        e.style.opacity   = '0';
        e.style.transform = '';
      });

      const cv = document.getElementById(`acim-cval-${sid}`);
      if (cv) cv.textContent = '0';

      Object.assign(this.currentVB, this.initialVB);
      this._applyVB();
    }

    // ── Timeline ──────────────────────────────────────────────────────────────

    _play() {
      const C   = this.C;
      const sid = this.sid;
      const mob = this._mobile;

      this.cObj = { val: 0 };
      this.playing = true;

      const tl = anime.timeline({
        autoplay: false,
        complete: () => {
          this.playing = false;
          if (C.loop) {
            setTimeout(() => { this._reset(); this._play(); }, 1800);
          }
        },
      });

      // 1. Linha — controla revelação de steps via update callback
      tl.add({
        targets: `#acim-line-${sid}`,
        strokeDashoffset: [this.svgLineLen, 0],
        duration: C.duration,
        easing:   C.easing,
        update:   () => {
          const raw   = parseFloat(this.line.style.strokeDashoffset);
          const drawn = this.svgLineLen - (isNaN(raw) ? this.svgLineLen : raw);

          C.steps.forEach((_, i) => {
            if (!this.stepRevealed[i] && drawn >= this.cumLen[i + 1]) {
              this.stepRevealed[i] = true;
              if (mob) {
                // Mobile: perspectiva — entra da direita (coluna do contador), escala como profundidade
                anime({
                  targets:    `#acim-step-${i}-${sid}`,
                  opacity:    [0, 1],
                  translateX: [120, 0],
                  scale:      [0.45, 1],
                  duration:   520,
                  easing:     'easeOutCubic',
                });
              } else {
                anime({
                  targets:  `#acim-step-${i}-${sid}`,
                  opacity:  [0, 1],
                  scale:    [0.2, 1],
                  duration: 420,
                  easing:   'easeOutBack',
                });
              }
            }
          });

          if (!this.endRevealed && drawn >= this.cumLen[this.allPts.length - 1] - 0.5) {
            this.endRevealed = true;
            if (mob) {
              anime({
                targets:    `#acim-end-${sid}`,
                opacity:    [0, 1],
                translateX: [80, 0],
                scale:      [0.4, 1],
                duration:   540,
                easing:     'easeOutCubic',
              });
            } else {
              anime({
                targets:  `#acim-end-${sid}`,
                opacity:  [0, 1],
                scale:    [0.3, 1],
                duration: 540,
                easing:   'easeOutBack',
              });
            }
          }
        },
      });

      // 2. Zoom-out paralelo à linha
      if (C.zoomOut.enabled) {
        tl.add({
          targets: this.currentVB,
          x: this.fullVB.x, y: this.fullVB.y,
          w: this.fullVB.w, h: this.fullVB.h,
          duration: C.duration,
          easing:   C.zoomOut.easing,
          update:   () => this._applyVB(),
        }, 0);
      }

      // 3. Counter — conta de 0 ao valor final, encerra 500ms após a linha
      tl.add({
        targets:  this.cObj,
        val:      C.end.counterValue,
        round:    1,
        duration: C.duration + 500,
        easing:   C.easing,
        update:   () => {
          const cv = document.getElementById(`acim-cval-${sid}`);
          if (cv) cv.textContent = this.cObj.val;
        },
      }, 0);

      this._timeline = tl;
      tl.play();
    }

    // ── Cancelar animações ────────────────────────────────────────────────────

    _cancelAll() {
      const sid = this.sid;
      anime.remove(`#acim-line-${sid}`);
      anime.remove(this.currentVB);
      anime.remove(this.cObj);
      this.C.steps.forEach((_, i) => anime.remove(`#acim-step-${i}-${sid}`));
      anime.remove(`#acim-end-${sid}`);
    }

    // ── Observers ─────────────────────────────────────────────────────────────

    _observe() {
      const sectionEl = this.svg.closest('[data-acium-motion]');
      if (!sectionEl) return;

      let lastMobile = this._mobile;

      // ResizeObserver: rebuild + replay ao trocar layout desktop ↔ mobile
      const ro = new ResizeObserver(() => {
        const nowMobile = this._mobile;
        if (nowMobile !== lastMobile) {
          lastMobile = nowMobile;
          this._cancelAll();
          this._computeCoords();
          this._buildSVG();
          this._computeLengths();
          this._setupZoom();
          this.stepRevealed = new Array(this.C.steps.length).fill(false);
          this.endRevealed  = false;
          this.playing      = false;
          this._play();
        }
      });
      ro.observe(sectionEl);

      // IntersectionObserver: dispara ao entrar no viewport
      const io = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.playing) {
            if (this.endRevealed) {
              this._cancelAll();
              this._reset();
            }
            this._play();
          }
        });
      }, { threshold: 0.15 });

      io.observe(sectionEl);
    }

    // ── Inicialização ─────────────────────────────────────────────────────────

    _init() {
      this._computeCoords();
      this._buildSVG();
      this._computeLengths();
      this._setupZoom();
      this.stepRevealed = new Array(this.C.steps.length).fill(false);
      this._observe();
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // AciumTimeline — timeline horizontal animada com carrossel mobile
  // ─────────────────────────────────────────────────────────────────────────
  class AciumTimeline {
    constructor(sid, config, trackEl, fillEl, itemsWrapEl) {
      this.sid     = sid;
      this._tlCfg  = config.tl || {};
      this.stagger = this._tlCfg.stagger || 170;
      this.track   = trackEl;
      this.fill    = fillEl;
      this.wrap    = itemsWrapEl;
      this.items   = Array.from(itemsWrapEl.querySelectorAll('.acim__tl-item'));
      this.played  = false;
      this._obs    = null;
      this._init();
    }

    get _mobile()   { return window.innerWidth < 750; }
    get _isSimpleM(){ return this._tlCfg.mobileStyle === 'simple'; }

    _overflow() {
      const cfg = this._tlCfg;
      return this._mobile
        ? (cfg.overflowMEnabled ? (cfg.overflowM || 0) : 0)
        : (cfg.overflowDEnabled ? (cfg.overflowD || 0) : 0);
    }

    _alignTrack() {
      const mob = this._mobile;
      if (mob && !this._isSimpleM) return; // card mode: track hidden, skip
      if (!this.items.length) return;
      const ov   = this._overflow();
      const cr   = this.track.parentElement.getBoundingClientRect();
      const fn   = this.items[0].querySelector('.acim__tl-node').getBoundingClientRect();
      const ln   = this.items[this.items.length - 1].querySelector('.acim__tl-node').getBoundingClientRect();
      const half = fn.width / 2;
      this.track.style.top   = (fn.top - cr.top + half) + 'px';
      this.track.style.left  = Math.max(0, fn.left  - cr.left  + half - ov) + 'px';
      this.track.style.right = Math.max(0, cr.right - ln.right + half - ov) + 'px';
    }

    _reset() {
      this.fill.style.transition = 'none';
      this.fill.classList.remove('go');
      this.items.forEach(i => i.classList.remove('go'));
    }

    _play() {
      this._reset();
      this._alignTrack();
      void this.fill.offsetWidth;
      setTimeout(() => {
        this.fill.style.transition = '';
        this.fill.classList.add('go');
      }, 60);
      this.items.forEach((item, i) => {
        setTimeout(() => item.classList.add('go'), 300 + i * this.stagger);
      });
      this.played = true;
    }

    _observe() {
      const sectionEl = document.getElementById(`acim-motion-${this.sid}`);
      if (!sectionEl || !('IntersectionObserver' in window)) { this._play(); return; }
      this._obs = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && !this.played) {
          this._play();
          this._obs.disconnect();
        }
      }, { threshold: 0.2 });
      this._obs.observe(sectionEl);
    }

    _snapActive() {
      if (!this._mobile || this._isSimpleM) return;
      const cx = this.wrap.scrollLeft + this.wrap.offsetWidth / 2;
      let closest = null, minD = Infinity;
      this.items.forEach(el => {
        const d = Math.abs((el.offsetLeft + el.offsetWidth / 2) - cx);
        if (d < minD) { minD = d; closest = el; }
      });
      this.items.forEach(el => el.classList.remove('snap-active'));
      if (closest) closest.classList.add('snap-active');
    }

    _bindAnchorGap() {
      this.items.forEach(item => {
        if (item.tagName !== 'A') return;
        const gap = parseInt(item.dataset.anchorGap || '0', 10);
        if (!gap) return;
        item.addEventListener('click', e => {
          const href = item.getAttribute('href');
          if (!href || !href.startsWith('#')) return;
          const target = document.getElementById(href.slice(1));
          if (!target) return;
          e.preventDefault();
          const top = target.getBoundingClientRect().top + window.scrollY + gap;
          window.scrollTo({ top, behavior: 'smooth' });
        });
      });
    }

    _init() {
      this._observe();
      this.wrap.addEventListener('scroll', () => this._snapActive(), { passive: true });
      setTimeout(() => this._snapActive(), 200);
      window.addEventListener('resize', () => { this._alignTrack(); });
      this._bindAnchorGap();
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Globe3D — globo 3D via canvas (projeção ortográfica)
  // ─────────────────────────────────────────────────────────────────────────
  class Globe3D {
    constructor(canvas, { size = 245, color = '#1a1a1a', speedSec = 16, numMeridians = 14, numLatLines = 5 } = {}) {
      this.canvas       = canvas;
      this.color        = color;
      this.speedSec     = Math.max(0.5, speedSec);
      this.numMeridians = numMeridians;
      this.numLatLines  = numLatLines;
      this.lineAlpha         = 0.25;
      this.meridianThreshold = 0.0;
      this.rotation          = 0;
      this._last = null; this._id = null;
      this._tick = this._tick.bind(this);
      this.resize(size);
      this._id = requestAnimationFrame(this._tick);
    }

    resize(size) {
      const dpr = window.devicePixelRatio || 1;
      this.canvas.width  = Math.round(size * dpr);
      this.canvas.height = Math.round(size * dpr);
      this.ctx = this.canvas.getContext('2d');
      this.ctx.scale(dpr, dpr);
      this.size = size; this.R = size / 2 - 1.2;
      this.cx   = size / 2; this.cy = size / 2;
    }

    _tick(ts) {
      if (this._last !== null) {
        const dt = Math.min((ts - this._last) / 1000, 0.1);
        this.rotation += (2 * Math.PI / this.speedSec) * dt;
      }
      this._last = ts; this._draw();
      this._id = requestAnimationFrame(this._tick);
    }

    _buildLatAngles() {
      const n = this.numLatLines;
      if (n <= 1) return [0];
      const step = 140 / (n - 1);
      return Array.from({ length: n }, (_, i) => -70 + i * step);
    }

    _draw() {
      const { ctx, cx, cy, R, color } = this;
      ctx.clearRect(0, 0, this.size, this.size);
      ctx.save();
      ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2); ctx.clip();
      ctx.fillStyle = 'white'; ctx.fillRect(0, 0, this.size, this.size);
      ctx.strokeStyle = color; ctx.globalAlpha = this.lineAlpha; ctx.lineWidth = 0.7;

      for (const latDeg of this._buildLatAngles()) {
        const lat = latDeg * Math.PI / 180;
        const y   = cy - R * Math.sin(lat);
        const rx  = R * Math.cos(lat);
        ctx.beginPath();
        if (latDeg >= 0) ctx.ellipse(cx, y, rx, rx * 0.18, 0, 0, Math.PI);
        else             ctx.ellipse(cx, y, rx, rx * 0.18, 0, Math.PI, 2 * Math.PI);
        ctx.stroke();
      }

      const n = this.numMeridians, thr = this.meridianThreshold;
      for (let i = 0; i < n; i++) {
        const theta = this.rotation + (i / n) * Math.PI * 2;
        const cosT  = Math.cos(theta), sinT = Math.sin(theta);
        if (cosT <= 0 || cosT < thr) continue;
        ctx.beginPath();
        ctx.ellipse(cx + R * sinT, cy, cosT * R, R, 0, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.restore();

      ctx.globalAlpha = this.lineAlpha; ctx.lineWidth = 1.1; ctx.strokeStyle = color;
      ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2); ctx.stroke();
      ctx.globalAlpha = 1;
    }

    destroy() { if (this._id) { cancelAnimationFrame(this._id); this._id = null; } }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // AciumBrandReveal — globo canvas 3D + Brasil + textos em sequência CSS
  // ─────────────────────────────────────────────────────────────────────────
  class AciumBrandReveal {
    constructor(sid, config, stageEl) {
      this.sid    = sid;
      this.C      = config.br || {};
      this.stage  = stageEl;
      this.played = false;
      this._initGlobe();
      this._observe();
    }

    _initGlobe() {
      const canvas = document.getElementById(`acim-br-canvas-${this.sid}`);
      if (!canvas) return;
      const mobile = window.innerWidth < 750;
      const size   = mobile ? (this.C.globeSizeM || 180) : (this.C.globeSize || 252);
      this.globe = new Globe3D(canvas, {
        size,
        color:        this.C.lineColor    || '#1a1a1a',
        speedSec:     this.C.speedSec     || 16,
        numMeridians: this.C.meridians    || 14,
        numLatLines:  this.C.latLines     || 5
      });
      this.globe.lineAlpha         = this.C.lineAlpha   != null ? this.C.lineAlpha   : 0.25;
      this.globe.meridianThreshold = this.C.meridianThr != null ? this.C.meridianThr : 0.68;
    }

    _play() {
      this.stage.classList.add('go');
      this.played = true;
    }

    _observe() {
      const wrapEl = document.getElementById(`acium-motion-${this.sid}`);
      if (!wrapEl || !('IntersectionObserver' in window)) { this._play(); return; }
      const io = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && !this.played) {
          this._play();
          io.disconnect();
        }
      }, { threshold: 0.2 });
      io.observe(wrapEl);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Descoberta e instanciação de sections
  // ─────────────────────────────────────────────────────────────────────────

  function initAciumMotion() {
    document.querySelectorAll('[data-acium-motion]:not([data-acim-ready])').forEach(section => {
      section.dataset.acimReady = '1';
      const sid = section.dataset.aciumMotion;

      const configEl = document.getElementById(`acim-config-${sid}`);
      if (!configEl) return;

      let config;
      try { config = JSON.parse(configEl.textContent); }
      catch (e) { console.error('AciumMotion: erro ao parsear config', sid, e); return; }

      if (config.animationType === 'infographic-line') {
        const svgEl     = document.getElementById(`acim-svg-${sid}`);
        const counterEl = document.getElementById(`acim-counter-${sid}`);
        if (!svgEl || !counterEl) return;
        section.__aciumInstance = new AciumInfoline(sid, config, svgEl, counterEl);
      } else if (config.animationType === 'timeline') {
        const trackEl = document.getElementById(`acim-tl-track-${sid}`);
        const fillEl  = document.getElementById(`acim-tl-fill-${sid}`);
        const wrapEl  = document.getElementById(`acim-tl-items-${sid}`);
        if (!trackEl || !fillEl || !wrapEl) return;
        section.__aciumInstance = new AciumTimeline(sid, config, trackEl, fillEl, wrapEl);
      } else if (config.animationType === 'brand-reveal') {
        const stageEl = document.getElementById(`acim-br-${sid}`);
        if (!stageEl) return;
        section.__aciumInstance = new AciumBrandReveal(sid, config, stageEl);
      }
    });
  }

  // Suporte ao theme editor
  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('[data-acium-motion][data-acim-ready]').forEach(el => {
      delete el.dataset.acimReady;
    });
    if (typeof anime !== 'undefined') initAciumMotion();
  });

  // ─────────────────────────────────────────────────────────────────────────
  // Carregar Anime.js via CDN (uma única vez) e depois iniciar
  // ─────────────────────────────────────────────────────────────────────────

  function _run() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initAciumMotion);
    } else {
      initAciumMotion();
    }
  }

  if (typeof anime !== 'undefined') {
    _run();
  } else if (!window.__aciumAnimeLoading) {
    window.__aciumAnimeLoading = true;
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/animejs/3.2.1/anime.min.js';
    s.onload = () => { window.__aciumAnimeLoading = false; _run(); };
    document.head.appendChild(s);
  } else {
    const wait = setInterval(() => {
      if (typeof anime !== 'undefined') { clearInterval(wait); _run(); }
    }, 50);
  }

} // end if !window.__AciumMotionDefined
